import { HighlightInfo } from 'schemas/HighlightInfoSchema'
import HighlightInfoType from 'enums/HighlightInfoType'
import messageUtil from 'utils/messageUtil'
import SoundbiteSentiment, { SoundbiteSentimentStrings } from 'enums/SoundbiteSentiment'
import ProsperoPostType from 'enums/ProsperoPostType'
import { StreamData } from 'types/streamData'
import { shouldAddZWJV2 } from 'utils/rtlTextUtil'
import i18n from 'i18n'
import { Metadata } from '../../adaptor/metadata'
import insertTagFromHighlightingMap from '../util/insertTagUtil'

const resolveTag = (
  focusSentence: HighlightInfo,
  annotatedSentiment?: SoundbiteSentimentStrings
) => {
  let spanTitle

  switch (focusSentence.type!) {
    case HighlightInfoType.RESULT:
      if (annotatedSentiment) {
        // CB-16194, V3 | Ad hoc annotation and Highlighting not syncing
        return ''
      }

      spanTitle = `${messageUtil.sentimentSingular(
        focusSentence.sentiment!
      )} ${messageUtil.highlightRoleInsight(focusSentence.insight!)}`

      switch (focusSentence.sentiment) {
        case SoundbiteSentiment.POSITIVE:
          return `<span class="color-sentiment-positive" title="${spanTitle}">`

        case SoundbiteSentiment.NEGATIVE:
          return `<span class="color-sentiment-negative" title="${spanTitle}">`

        default:
          return ''
      }

    case HighlightInfoType.SEARCH_TERM:
      return `<span class="primary-term" title="${i18n.t('primaryTerm')}">`

    default:
      return ''
  }
}

const resolveHighlightingRelatedFields = (metadata: Metadata) => {
  const highlightedSentences: string[] = []
  const highlightingMaps: { [key: number]: string }[] = []

  const highlightingInfos = metadata.getHighlightingInfos()
  if (!highlightingInfos) {
    return { highlightingMaps } as StreamData
  }

  highlightingInfos.forEach((highlightingInfo, index) => {
    highlightedSentences.push(metadata.getSentences()![index])

    const map: { [key: number]: string } = {}
    if (!highlightingInfo) {
      highlightingMaps.push(map)
      return
    }

    const displayTextRange = metadata.getDisplayTextRange()
    highlightingInfo
      .focusSentence!.filter((focusSentence) => {
        if (
          displayTextRange &&
          displayTextRange[index] &&
          displayTextRange[index]!.startOffset! > focusSentence.startOffset!
        ) {
          return false
        }

        if (metadata.getPostType()! === ProsperoPostType.TW_RETWEETS) {
          // Not highlight: RT @OriginalAuthor:
          const RTLength = 6 + metadata.getOriginalAuthor()!.length
          if (focusSentence.startOffset! < RTLength) {
            return false
          }
          // skip highlighting on the reply-to handles(when it's a RT post)
          if (
            displayTextRange &&
            displayTextRange[index] &&
            displayTextRange[index]!.startOffset! > focusSentence.startOffset! - RTLength
          ) {
            return false
          }
        }

        return true
      })
      .forEach((focusSentence) => {
        let tag = resolveTag(focusSentence, metadata.getAnnotatedSentiment())

        if (tag) {
          const prependZWJ = shouldAddZWJV2(highlightedSentences[index], focusSentence.startOffset!)
          const appendZWJ = shouldAddZWJV2(highlightedSentences[index], focusSentence.endOffset!)
          let closeTag = '</span>'

          if (prependZWJ) {
            tag = `&#x200d;${tag}&#x200d;`
          }
          if (appendZWJ) {
            closeTag = `&#x200d;${closeTag}&#x200d;`
          }

          // open tag
          map[focusSentence.startOffset!] = map[focusSentence.startOffset!] || ''
          map[focusSentence.startOffset!] =
            focusSentence.type === HighlightInfoType.SEARCH_TERM
              ? map[focusSentence.startOffset!] + tag
              : tag + map[focusSentence.startOffset!]

          // close tag
          map[focusSentence.endOffset!] = map[focusSentence.endOffset!] || ''
          map[focusSentence.endOffset!] = map[focusSentence.endOffset!] + closeTag
        }
      })

    highlightingMaps.push(map)
    highlightedSentences[index] = insertTagFromHighlightingMap(highlightedSentences[index], map)
  })

  return { highlightedSentences, highlightingMaps } as StreamData
}

export default resolveHighlightingRelatedFields
